import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import gsap from "gsap";
import { useLenis } from "lenis/react";
import { SERVICES, SITE, COMPARE_WIDE, contactPath } from "../data/site";
import Seo, { localBusinessJsonLd } from "../Components/Seo";
import BeforeAfter from "../Components/BeforeAfter";
import Picture from "../Components/Picture";
import NotFound from "./NotFound";

function serviceAssets(service) {
  if (!service) return [];
  if (service.compare) {
    return [
      service.before?.webp,
      service.before?.fallback,
      service.after?.webp,
      service.after?.fallback,
    ].filter(Boolean);
  }
  return [service.hero, service.heroFallback].filter(Boolean);
}

function preloadImages(urls, timeoutMs = 450) {
  const unique = [...new Set(urls)];
  if (!unique.length) return Promise.resolve();

  const load = Promise.all(
    unique.map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = src;
        })
    )
  );

  return Promise.race([
    load,
    new Promise((resolve) => window.setTimeout(resolve, timeoutMs)),
  ]);
}

function scrollToTop(lenis) {
  const y = lenis?.scroll ?? window.scrollY ?? 0;
  if (y < 10) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    if (lenis) {
      const duration = Math.min(1, Math.max(0.42, y / 2200));
      lenis.scrollTo(0, {
        duration,
        force: true,
        easing: (t) => 1 - (1 - t) ** 3,
        onComplete: done,
      });
      window.setTimeout(done, duration * 1000 + 120);
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
    window.setTimeout(done, 700);
  });
}

function ServiceHeading({ title }) {
  const parts = title.split(" & ");
  if (parts.length === 1) return title;
  return parts.map((part, i) =>
    i === 0 ? (
      part
    ) : (
      <span key={part} className="nowrap">
        {" "}
        & {part}
      </span>
    )
  );
}

function ServiceView({ service, autoPlay = true }) {
  return (
    <>
      <header className="page-hero">
        <div className="wrap">
          <p className="kicker" style={{ color: "var(--cyan)" }}>
            Leistung {service.number}
          </p>
          <h1 className="display service-heading">
            <ServiceHeading title={service.title} />
          </h1>
          <p className="service-lead">
            <ServiceHeading title={service.lead} />
          </p>
        </div>
      </header>
      <section className="section">
        <div className="wrap split">
          {service.compare ? (
            <BeforeAfter
              before={service.before}
              after={service.after}
              wide={COMPARE_WIDE}
              alt={`${service.title} Vorher und Nachher`}
              autoPlay={autoPlay}
            />
          ) : (
            <Picture
              className="service-still"
              src={service.hero}
              fallback={service.heroFallback}
              alt={service.title}
              width={1200}
              height={800}
              loading="eager"
              fetchPriority="high"
            />
          )}
          <div className="prose">
            <p>{service.body}</p>
            <ul className="check-list">
              {service.points.map((p) => (
                <li key={p}>
                  <span aria-hidden="true">▸</span>
                  {p}
                </li>
              ))}
            </ul>
            {service.sections?.map((block) => (
              <div key={block.title}>
                <h3>{block.title}</h3>
                {block.text ? <p>{block.text}</p> : null}
                {block.items ? (
                  <ul className="check-list">
                    {block.items.map((item) => (
                      <li key={item}>
                        <span aria-hidden="true">▸</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
            <div className="hero-actions">
              <Link className="btn btn-navy" to={contactPath(service.slug)}>
                Angebot anfragen
              </Link>
              <a className="btn btn-line" href={`tel:${SITE.phoneTel}`}>
                {SITE.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <p className="kicker">Weitere Leistungen</p>
          <div className="service-list" style={{ marginTop: "1rem" }}>
            {SERVICES.filter((s) => s.slug !== service.slug).map((s) => (
              <Link key={s.slug} to={`/leistungen/${s.slug}`} className="service-row">
                <span className="service-num">{s.number}</span>
                <span className="service-title">{s.title}</span>
                <span className="service-go">Ansehen →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default function ServicePage() {
  const { slug } = useParams();
  const next = SERVICES.find((s) => s.slug === slug);
  const [shown, setShown] = useState(next);
  const [leaving, setLeaving] = useState(null);
  const shownLayer = useRef(null);
  const leaveLayer = useRef(null);
  const shownSlugRef = useRef(next?.slug);
  const runId = useRef(0);
  const lenis = useLenis();
  const lenisRef = useRef(lenis);
  lenisRef.current = lenis;

  useEffect(() => {
    if (!next) {
      shownSlugRef.current = undefined;
      setShown(undefined);
      setLeaving(null);
      return;
    }

    if (next.slug === shownSlugRef.current) return;

    const id = ++runId.current;
    const dest = next;
    const from = SERVICES.find((s) => s.slug === shownSlugRef.current);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    gsap.killTweensOf([shownLayer.current, leaveLayer.current]);
    if (shownLayer.current) gsap.set(shownLayer.current, { clearProps: "opacity" });
    setLeaving(null);

    if (reduced) {
      shownSlugRef.current = dest.slug;
      setLeaving(null);
      setShown(dest);
      const scroller = lenisRef.current;
      if (scroller) scroller.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
      return;
    }

    let cancelled = false;

    (async () => {
      await Promise.all([scrollToTop(lenisRef.current), preloadImages(serviceAssets(dest))]);
      if (cancelled || id !== runId.current) return;

      shownSlugRef.current = dest.slug;
      setLeaving(from ?? null);
      setShown(dest);
      if (!from) {
        const enter = shownLayer.current;
        if (enter) gsap.set(enter, { clearProps: "opacity" });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [next]);

  useLayoutEffect(() => {
    if (!leaving || !shown || leaving.slug === shown.slug) {
      return;
    }

    const enter = shownLayer.current;
    const leave = leaveLayer.current;
    if (!enter || !leave) {
      setLeaving(null);
      return;
    }

    gsap.set(leave, { opacity: 1 });
    gsap.set(enter, { opacity: 0 });

    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set(enter, { clearProps: "opacity" });
        setLeaving(null);
      },
    });

    tl.to(leave, { opacity: 0, duration: 0.42, ease: "power2.inOut" }, 0);
    tl.to(enter, { opacity: 1, duration: 0.5, ease: "power2.out" }, 0.04);

    return () => {
      tl.kill();
      if (shownLayer.current) gsap.set(shownLayer.current, { clearProps: "opacity" });
    };
  }, [leaving, shown]);

  if (!next) return <NotFound />;
  const service = shown ?? next;

  const jsonLd = [
    localBusinessJsonLd(),
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: next.title,
      provider: { "@type": "HomeAndConstructionBusiness", name: SITE.name },
      areaServed: SITE.area,
      description: next.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE.url },
        {
          "@type": "ListItem",
          position: 2,
          name: next.title,
          item: `${SITE.url}/leistungen/${next.slug}`,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${next.title} in Mülheim & Essen`}
        description={next.description}
        path={`/leistungen/${next.slug}`}
        image={next.imageFallback}
        jsonLd={jsonLd}
      />
      <div className={`service-stage${leaving ? " is-switching" : ""}`}>
        {leaving ? (
          <div className="service-layer is-leave" ref={leaveLayer} aria-hidden="true" inert>
            <ServiceView key={leaving.slug} service={leaving} autoPlay={false} />
          </div>
        ) : null}
        <div className="service-layer" ref={shownLayer}>
          <ServiceView key={service.slug} service={service} autoPlay={!leaving} />
        </div>
      </div>
    </>
  );
}
